/* eslint-disable max-len */
/* eslint-disable react/destructuring-assignment */
import React, { useMemo, useState } from 'react';
import classnames from 'classnames';
import { resolve } from '@leverege/plugin';
import { Flex, Text, Button, Checkbox } from '@leverege/ui-elements';
import { useValueChange } from '@leverege/ui-hooks';
import { Toolbar } from '@leverege/ui-plugin';
import I18N from '@leverege/i18n';
import { R } from '@leverege/value-resolver';
import ViewerModel from "./ViewerModel.js";
import DataViewerModel from "../DataViewerModel.js";
import Util from "./Util.js";
import SectionSelector from "./SectionSelector.js";
import S from './ViewerSelector.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const CLIENT = 'LayoutEditor';
const {
  t,
  tIcon
} = I18N.ns('molten.dataViewers.layout.ViewerSelector');
export default function ViewerSelector(props) {
  const {
    value,
    relationship,
    onChange,
    eventData,
    dataViewers,
    className,
    context
  } = props;
  const isItem = context.client === 'ItemInfoScreen';
  const settingsProps = props.settingsProps || {
    objectType: relationship.objectType,
    relationship,
    dataViewers
  };
  const onValueChange = useValueChange(ViewerModel, props);
  const [hover, setHover] = useState(null);
  const viewers = useMemo(() => {
    return {
      main: [{
        type: null,
        icon: tIcon('banIcon'),
        name: 'None',
        sort: '0'
      }, ...dataViewers.getFor('main', false, context)],
      aux: [{
        type: null,
        icon: tIcon('banIcon'),
        name: 'None',
        sort: '0'
      }, ...dataViewers.getFor('aux', false, context)]
    };
  }, [context, dataViewers]);
  const layout = value.layout || 'vh';
  const gridTemplateAreas = ViewerModel.createGridTemplate(value, true);
  let over;
  if (hover) {
    over = dataViewers.getByType(ViewerModel[hover](value));
  }
  const aux1 = ViewerModel.getAux1(value);
  const aux2 = ViewerModel.getAux2(value);
  const aux3 = ViewerModel.getAux3(value);
  const aux4 = ViewerModel.getAux4(value);
  const main1 = ViewerModel.getMain1(value);
  const main2 = ViewerModel.getMain2(value);
  const topLeft = ViewerModel.getTopLeft(value) || 'aux1';
  const topRight = ViewerModel.getTopRight(value) || 'aux1';
  const bottomRight = ViewerModel.getBottomRight(value) || 'aux2';
  const bottomLeft = ViewerModel.getBottomLeft(value) || 'aux2';
  const showFilterBar = ViewerModel.getShowFilterBar(value);
  const onExpand = evt => {
    const {
      data
    } = evt;
    const curr = ViewerModel[`get${data}`](value) || (data === 'TopLeft' ? 'aux1' : 'aux3');
    let next;
    switch (data) {
      case 'TopLeft':
        next = curr === 'aux1' ? 'aux3' : 'aux1';
        break;
      case 'TopRight':
        next = curr === 'aux1' ? 'aux4' : 'aux1';
        break;
      case 'BottomLeft':
        next = curr === 'aux2' ? 'aux3' : 'aux2';
        break;
      case 'BottomRight':
        next = curr === 'aux2' ? 'aux4' : 'aux2';
        break;
      default:
        return;
    }
    const nv = ViewerModel[`set${data}`](value, next);
    onChange({
      data: eventData,
      value: nv
    });
  };
  const onRotate = evt => {
    const curr = ViewerModel.getLayout(value) || 'vh';
    let nv = ViewerModel.setLayout(value, curr[0] + (curr[1] === 'h' ? 'v' : 'h'));
    if (curr[1] === 'v') {
      const a = ViewerModel.getMain1(nv);
      const b = ViewerModel.getMain2(nv);
      nv = ViewerModel.setMain1(nv, b);
      nv = ViewerModel.setMain2(nv, a);
    }
    onChange({
      data: eventData,
      value: nv
    });
  };
  return /*#__PURE__*/_jsxs(Flex, {
    variant: "colM",
    className: classnames(S.editor, className),
    children: [!isItem && /*#__PURE__*/_jsx(Checkbox, {
      label: t('showFilterBar'),
      value: showFilterBar,
      onChange: onValueChange,
      eventData: "setShowFilterBar"
    }), /*#__PURE__*/_jsxs("div", {
      style: {
        '--layoutGrid': gridTemplateAreas
      },
      className: classnames(S.grid, S[layout]),
      children: [/*#__PURE__*/_jsx("div", {
        className: classnames(S.area, S.aux, S.aux1),
        onMouseEnter: () => setHover('getAux1'),
        onMouseLeave: () => setHover(null),
        children: selector(onValueChange, 'setAux1', aux1, viewers.aux, [aux2, main1, main2, aux3, aux4], dataViewers, settingsProps)
      }), /*#__PURE__*/_jsx("div", {
        className: classnames(S.area, S.main, S.main1),
        onMouseEnter: () => setHover('getMain1'),
        onMouseLeave: () => setHover(null),
        children: selector(onValueChange, 'setMain1', main1, viewers.main, [aux1, main2, aux2, aux3, aux4], dataViewers, settingsProps)
      }), /*#__PURE__*/_jsx("div", {
        className: classnames(S.area, S.main, S.main2),
        onMouseEnter: () => setHover('getMain2'),
        onMouseLeave: () => setHover(null),
        children: selector(onValueChange, 'setMain2', main2, viewers.main, [aux1, main1, aux2, aux3, aux4], dataViewers, settingsProps)
      }), /*#__PURE__*/_jsx("div", {
        className: classnames(S.area, S.aux, S.aux2),
        onMouseEnter: () => setHover('getAux2'),
        onMouseLeave: () => setHover(null),
        children: selector(onValueChange, 'setAux2', aux2, viewers.aux, [aux1, main1, main2, aux3, aux4], dataViewers, settingsProps)
      }), /*#__PURE__*/_jsx("div", {
        className: classnames(S.area, S.aux, S.aux3),
        onMouseEnter: () => setHover('getAux3'),
        onMouseLeave: () => setHover(null),
        children: selector(onValueChange, 'setAux3', aux3, viewers.aux, [aux1, main1, main2, aux2, aux4], dataViewers, settingsProps)
      }), /*#__PURE__*/_jsx("div", {
        className: classnames(S.area, S.aux, S.aux4),
        onMouseEnter: () => setHover('getAux4'),
        onMouseLeave: () => setHover(null),
        children: selector(onValueChange, 'setAux4', aux4, viewers.aux, [aux1, main1, main2, aux2, aux3], dataViewers, settingsProps)
      }), /*#__PURE__*/_jsx("div", {
        className: S.rotateMain,
        children: /*#__PURE__*/_jsx(Button, {
          variant: "link",
          onClick: onRotate,
          icon: "fa fa-rotate-right fa-fw"
        })
      }), /*#__PURE__*/_jsx("div", {
        className: S.tlExpand,
        children: /*#__PURE__*/_jsx(Button, {
          variant: "link",
          className: S.expand,
          eventData: "TopLeft",
          onClick: onExpand,
          icon: `fa fa-long-arrow-right ${topLeft === 'aux1' ? '' : 'fa-rotate-90'} fa-fw`
        })
      }), /*#__PURE__*/_jsx("div", {
        className: S.trExpand,
        children: /*#__PURE__*/_jsx(Button, {
          variant: "link",
          className: S.expand,
          eventData: "TopRight",
          onClick: onExpand,
          icon: `fa fa-long-arrow-left ${topRight === 'aux1' ? '' : S.neg90} fa-fw`
        })
      }), /*#__PURE__*/_jsx("div", {
        className: S.blExpand,
        children: /*#__PURE__*/_jsx(Button, {
          variant: "link",
          className: S.expand,
          eventData: "BottomLeft",
          onClick: onExpand,
          icon: `fa fa-long-arrow-right ${bottomLeft === 'aux2' ? '' : S.neg90} fa-fw`
        })
      }), /*#__PURE__*/_jsx("div", {
        className: S.brExpand,
        children: /*#__PURE__*/_jsx(Button, {
          variant: "link",
          className: S.expand,
          eventData: "BottomRight",
          onClick: onExpand,
          icon: `fa fa-long-arrow-left ${bottomRight === 'aux2' ? '' : 'fa-rotate-90'} fa-fw`
        })
      })]
    }), /*#__PURE__*/_jsx(Text, {
      variant: "caption",
      style: {
        textAlign: 'center',
        visibility: over !== undefined ? 'visible' : 'hidden'
      },
      children: over ? R(over.name) : 'None Selected'
    })]
  });
}
function selector(onValueChange, slot, value, dVs, exclude, dataViewers, settingsProps) {
  const values = dataViewers.filterOutUsed(dVs, exclude.filter(f => f !== value));
  const onChangeIntercept = e => {
    const {
      value,
      data
    } = e;
    if (value == null) {
      onValueChange({
        value: null,
        data
      });
      return;
    }
    let dv = values.find(v => {
      return v.type === value;
    });
    const settings = typeof dv?.createSettings === 'function' ? dv.createSettings(resolve(settingsProps)) : null;
    dv = DataViewerModel.create(value, settings);
    onValueChange({
      value: dv,
      data
    });
  };
  const v = typeof value === 'string' ? value : value?.type || null;
  return /*#__PURE__*/_jsx(SectionSelector, {
    toggleVariant: "viewerSelector",
    eventData: slot,
    icon: v => v?.icon || dataViewers.getByType(value)?.icon,
    value: v,
    valueKey: "type",
    values: values,
    onChange: onChangeIntercept
  });
}

// eslint-disable-next-line max-len
const VH = "data:image/svg+xml,%3csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3e %3cpath d='M0 1C0 0.447716 0.447715 0 1 0H23C23.5523 0 24 0.447715 24 1V23C24 23.5523 23.5523 24 23 24H1C0.447716 24 0 23.5523 0 23V1Z' fill='white'/%3e %3crect x='2' y='2' width='20' height='3' rx='0.5' fill='%23C4C4C4'/%3e %3crect x='2' y='7' width='9' height='10' rx='0.5' fill='%23C4C4C4'/%3e %3crect x='13' y='7' width='9' height='10' rx='0.5' fill='%23C4C4C4'/%3e %3crect x='2' y='19' width='20' height='3' rx='0.5' fill='%23C4C4C4'/%3e %3c/svg%3e";
function getLayoutIcon() {
  return VH;
}
ViewerSelector.getLayoutIcon = getLayoutIcon;
ViewerSelector.allowEditing = Util.allowEditing;
ViewerSelector.Popup = function ViewerSelectorPopup(props) {
  const {
    client = CLIENT,
    parentClient,
    relationship,
    settingsPath
  } = props;
  const {
    objectType
  } = relationship;
  const versionsPath = `${objectType}.${parentClient}.layout`;
  const matchContext = {
    use: 'actionBar',
    client,
    objectType
  };
  const context = {
    client,
    objectType,
    clientProps: {
      ...props,
      versionsPath,
      settingsPath,
      objectType,
      client
    }
  };
  return /*#__PURE__*/_jsxs(Flex, {
    direction: "column",
    children: [/*#__PURE__*/_jsx(ViewerSelector, {
      ...props,
      context: props.context,
      className: S.popup
    }), /*#__PURE__*/_jsx(Flex, {
      justify: "flex-end",
      children: /*#__PURE__*/_jsx(Toolbar, {
        variant: "actionBar",
        prefer: "icon",
        matchContext: matchContext,
        context: context
      })
    })]
  });
};